import { useMemo } from "react";

import { ITEM_TYPE_COLLECTION } from "@/const";
import { CollectionResponse } from "@/api";
import {
  ContentGrid,
  ContentThumbnail,
  Description,
  ResourcesButton,
} from "@/components";
import { useResponsiveness, useUser } from "@/providers/context";
import { cn, isCollectionOfCollections } from "@/utils";

import { PageOfSliders } from "./PageOfSliders";
import { SaveToPlaylistButton } from "../../Playlists";

interface DefaultCollectionProps {
  collection: CollectionResponse;
}

export const DefaultCollection = ({ collection }: DefaultCollectionProps) => {
  const { drawerMargin } = useResponsiveness();
  const { uid } = useUser();
  
  /*
   * Responsive Breakpoint is xl (minus the drawer width if open),
   * below this size the thumbnail is vertically stacked above the Title/Description and resizes
   * above this size they share the horizontal space, the thumbnail height is fixed
   */
  const contentWidth =
    window.innerWidth - (drawerMargin ? parseInt(drawerMargin) : 0);
  const isWideView = contentWidth > 1280;

  const isOnlyCollections = useMemo(() => {
    // All are tab/collection items
    if (collection) {
      return isCollectionOfCollections(collection);
    }
    return false;
  }, [collection]);

  return (
    <div>
      <div className={cn(["mb-[56px]", isWideView && "flex flex-row-reverse"])}>
        <div
          className={cn([
            "mb-4 flex aspect-video flex-shrink justify-end overflow-hidden",
            isWideView && "mb-0 w-[52%]",
          ])}
        >
          <ContentThumbnail
            thumbnail={
              collection.Thumbnails["large"] ??
              collection.Thumbnails["source"] ??
              ""
            }
            className="h-fit w-full rounded-[12px]"
          />
        </div>
        <div
          className={cn([
            "mr-8 flex min-w-[300px] flex-grow flex-col",
            isWideView && "w-[512px]",
          ])}
        >
          <div className="h4-bold mb-3 line-clamp-2 h-fit min-h-7 overflow-ellipsis">
            {collection.Name}
          </div>
          <div className="p2-regular">
            <Description desc={collection.Description} />
          </div>
          {!!uid && (
            <div className="mt-7 flex gap-3">
              <SaveToPlaylistButton
                ItemId={collection.TabId}
                ItemType={ITEM_TYPE_COLLECTION}
                SourceItemId={collection.SourceId}
              />
              <ResourcesButton resources={collection.Resources} />
            </div>
          )}
        </div>
      </div>
      <div>
        {isOnlyCollections ? (
          <PageOfSliders collectionId={collection.TabId} />
        ) : (
          <ContentGrid
            items={collection.Items}
            collection={collection}
            fullItems={{
              videos: collection.ChildVideos,
              collections: collection.ChildCollections,
            }}
          />
        )}
      </div>
    </div>
  );
};
